/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { SelectMobileFieldInput } from '#PRIVATE_SelectCommon';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { ComponentChildren, ComponentProps, Fragment, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { isBeforeDataFetch, isPhone, isTablet } from '../PRIVATE_SelectCommon';
import { DataState } from '../UNSAFE_Collection';
import { Label } from '../UNSAFE_Label';
import { ReadonlyTextField, ReadonlyTextFieldInput, TextFieldInput } from '../UNSAFE_TextField';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  UserAssistanceDensityType
} from '../UNSAFE_UserAssistance';
import { VirtualizedListView } from '../UNSAFE_VirtualizedListView';
import { FocusableHandle, useFocusableTextField } from '../hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useLoadingIndicatorTimer } from '../hooks/UNSAFE_useLoadingIndicatorTimer';
import { useTextField } from '../hooks/UNSAFE_useTextField';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { Item } from '../utils/UNSAFE_dataProvider';
import { ItemTextType } from '../utils/UNSAFE_selectUtils';
import { Size } from '../utils/UNSAFE_size';
import { LayoutColumnSpan } from '../utils/UNSAFE_styles/Layout';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { ItemRendererProps } from './SelectMultiple.types';
import { SelectMultipleDesktopDropdown } from './SelectMultipleDesktopDropdown';
import { SelectMultipleField } from './SelectMultipleField';
import { SelectMultipleFieldInput } from './SelectMultipleFieldInput';
import { SelectMultipleMobileDropdown } from './SelectMultipleMobileDropdown';
import { useDisplayValues } from './useDisplayValues';
import { useSelectMultiple } from './useSelectMultiple';

type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;
type SelectMultipleFieldInputProps = ComponentProps<typeof SelectMultipleFieldInput>;

type Props<K extends string | number, D extends Record<string, any>> = TestIdProps & {
  /**
   * Specifies whether the add to list link is shown in the dropdown when the user filters
   * and there are no results.
   * It is up to the application to show its own UI when the link is triggered.
   */
  addToList?: 'on' | 'off';
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: TextFieldInputProps['aria-describedby'];
  /**
   * Text to provide guidance to help the user understand what data to enter or select.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Specifies data for the dropdown list.
   *
   * Note that the <code>itemText</code> property allows for customizing the rendering
   * of each data item.
   */
  data?: DataState<K, D> | null;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies whether the component is waiting for <code>valueItems</code> to load.
   * While loading, SelectMultiple will show the progressive loading indicator in the text field.
   */
  isLoading?: boolean;
  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;
  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * selected by the user.  Setting this property to <code>true</code> means that a value
   * is required to be selected by the user.
   */
  isRequired?: TextFieldInputProps['isRequired'];
  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has selected a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];
  /**
   * Render function for an item in the default list in the dropdown.
   */
  itemRenderer?: (itemRendererProps: ItemRendererProps<K, D>) => ComponentChildren;
  /**
   * Specifies how to get the text string to render for a data item.
   * This property can be set to either:
   * <ul>
   * <li>a string that specifies the name of a top level data attribute to render as text, or</li>
   * <li>a callback function that takes a properties object and returns the text string to
   * display.</li>
   * </ul>
   *
   * This text will be rendered for the selected <code>valueItems</code> of the component.
   * It will also be rendered for each item in the dropdown.  When rendered for the
   * dropdown items, default matching search term highlighting will still be applied.
   */
  itemText: ItemTextType<K, D>;
  /**
   * Specifies the label associated with the field.
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * A short hint to display before the user selects or enters a value.
   */
  placeholder?: TextFieldInputProps['placeholder'];
  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextFieldInputProps['textAlign'];
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * Specifies the keys, data, and optional metadata for the selected values.
   */
  valueItems?: Item<K, D>[];
  /**
   * The style variant of the component.
   */
  variant?: TextFieldInputProps['variant'];
  /**
   * The type of virtual keyboard to display for entering a value on mobile browsers.
   * This property has no effect on desktop browsers.
   */
  virtualKeyboard?: SelectMultipleFieldInputProps['virtualKeyboard'];
  /**
   * Callback function called when the user triggers the add to list link shown in the dropdown.
   */
  onAddToListAction?: ({ searchText }: { searchText?: string }) => void;
  /**
   * Callback invoked when the user commits the selected values.
   *
   * The function should set a new array as the <code>valueItems</code> property.
   */
  onCommit: (detail: ValueUpdateDetail<Set<K>>) => void;
  /**
   * Callback function to trigger loading data for the dropdown list, which may or may not be
   * filtered by user entered search text.
   *
   * The function should set a new <code>DataState</code> as the <code>data</code> property.
   */
  onFilter?: ({ searchText }: { searchText?: string }) => void;
  /**
   * Callback function to handle when the viewport of the dropdown list has changed, for example
   * by scrolling, including the case where the user scrolls to the end of the list and there are
   * more items to load.
   *
   * The function should set a new <code>DataState</code> for the specified range as the
   * <code>data</code> property.
   */
  onLoadRange?: ComponentProps<typeof VirtualizedListView>['onLoadRange'];
};

/**
 * A SelectMultiple is a dropdown list that supports multiple selection and search filtering.
 */
export const SelectMultiple = forwardRef(
  <K extends string | number, D extends Record<string, any>>(
    {
      addToList = 'off',
      'aria-describedby': propAriaDescribedBy,
      assistiveText,
      columnSpan,
      data: propData,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isLoading: propIsLoading,
      isReadonly: propIsReadonly,
      isRequired,
      isRequiredShown,
      itemRenderer,
      itemText,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      onAddToListAction,
      onCommit,
      onFilter,
      onLoadRange: propOnLoadRange,
      placeholder,
      testId,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      valueItems,
      variant = 'default',
      virtualKeyboard
    }: Props<K, D>,
    ref?: Ref<FocusableHandle>
  ) => {
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const {
      enabledElementRef,
      focusProps,
      isFocused: origIsFocused,
      readonlyElementRef
    } = useFocusableTextField<HTMLElement, HTMLDivElement>({ isDisabled, isReadonly, ref });

    const {
      addToListEventHandlers,
      ariaProps,
      collectionProps,
      data,
      dropdownArrowEventHandlers,
      dropdownEventHandlers,
      dropdownRef,
      hasSelectedValuesCount,
      inputEventHandlers,
      isAddToListShown,
      isDataFetched,
      isDropdownAbove,
      isDropdownOpen,
      isDropdownSelectedOnlyView,
      isEmptyResults,
      isFocused,
      isUserFiltering,
      mainFieldRef,
      mouseProps,
      searchText,
      selectedValuesCountProps,
      selectedValuesDescriptionId,
      setDropdownOpen,
      setUserInput,
      stopFiltering,
      textTagListProps,
      userInput
    } = useSelectMultiple<K, D>({
      addToList,
      ariaDescribedBy: propAriaDescribedBy,
      data: propData,
      inputRef: enabledElementRef,
      isDisabled,
      isFocused: origIsFocused,
      isReadonly,
      onAddToListAction,
      onCommit,
      onFilter,
      onLoadRange: propOnLoadRange,
      valueItems
    });

    // The incoming propIsLoading tells us that we are in a loading state, but we don't want to
    // show the loading indicator until after a delay, because showing it immediately could result
    // in unwanted flashing.  The isLoading var below will be set to true by useLoadingIndicatorTimer
    // after the delay, when the timer expires, at which point we should show the loading indicator.
    // If propIsLoading is set to false before the timer expires, then the timer will be cancelled
    // and no loading indicator will be shown.
    // (ListView detects its loading state by checking data === null.  While it would be nice for
    // us to be consistent and check valueItems === null, we need to use a separate isLoading prop.
    // If valueItems === null triggers our loading state, then the selections in the dropdown
    // are cleared for that period of time, which is undesirable.)
    const isLoading = useLoadingIndicatorTimer(propIsLoading ?? false);

    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy: ariaProps.ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isFocused,
      isLoading,
      isReadonly,
      isRequiredShown,
      labelEdge,
      messages,
      styleVariant: variant,
      userAssistanceDensity,
      value: valueItems !== undefined && valueItems.length > 0 ? true : undefined
    });

    // JET-54256 - inside label animates up on initial display with selected value
    // If we're waiting for the selected values to load, the component should look like it
    // has a value in the meantime.
    // Use the original propIsLoading instead of isLoading because we don't want to delay
    // the inside label position by a timer like we do the progressive loading indicator.
    formFieldContext.hasValue = propIsLoading || formFieldContext.hasValue;

    const dropdownId = `${baseId}-dropdown`;

    const { displayValue, textTags } = useDisplayValues(itemText, valueItems);

    // Do not set a forId in phone as we will be using aria-labelledby instead.
    // This is because we will using a div and not input when rendering in a phone.
    const labelComp =
      labelEdge !== 'none' ? (
        <Label {...labelProps} {...(isPhone() ? { forId: undefined } : {})}>
          {label}
        </Label>
      ) : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={mainFieldRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

    if (isReadonly) {
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            columnSpan={columnSpan}
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onfocusout}
            onFocus={focusProps.onfocusin}
            ref={mainFieldRef}
            testId={testId}
            {...fieldLabelProps}>
            <ReadonlyTextFieldInput
              aria-describedby={inputProps['aria-describedby']}
              aria-label={ariaLabel}
              aria-labelledby={labelProps.id}
              as="div"
              elementRef={readonlyElementRef}
              hasEmptyLabel={label === '' && labelEdge === 'none'}
              hasInsideLabel={label !== undefined && labelEdge === 'inside'}
              textAlign={textAlign}
              value={displayValue}
            />
          </ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    const hasInsideLabel = labelEdge === 'inside';
    const liveRegionText =
      isDropdownOpen && isDataFetched
        ? data?.totalSize === 0
          ? translations.select_noMatchesFound()
          : data?.totalSize === 1
          ? translations.select_oneMatchFound()
          : data?.sizePrecision === 'exact'
          ? translations.select_sizeMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
          : translations.select_sizeOrMoreMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
        : '';

    const shouldShowDropdown =
      isDropdownOpen &&
      data !== undefined &&
      (data === null || data.totalSize > 0 || isBeforeDataFetch<K, D>(data) || isEmptyResults);

    const hasEmptyLabel = label === '' && labelEdge === 'none';

    // Use the mobile field if the component is being rendered in a phone
    // and it is not disabled.
    const isPhoneMainFieldInput = isPhone() && !isDisabled;
    const mainFieldInput = !isPhoneMainFieldInput ? (
      <SelectMultipleFieldInput
        aria-controls={dropdownId}
        aria-expanded={isDropdownOpen}
        aria-label={ariaLabel}
        displayValue={displayValue}
        hasEmptyLabel={hasEmptyLabel}
        hasInsideLabel={hasInsideLabel}
        inputRef={enabledElementRef as Ref<HTMLInputElement>}
        isAddToListShown={isAddToListShown}
        isRequired={isRequired}
        isTextTagListShown={isFocused && textTags.length !== 0}
        isUserFiltering={isUserFiltering}
        liveRegionText={liveRegionText}
        placeholder={placeholder}
        // we only want the remove icon in the main field for tablets
        removeIcon={isTablet() ? 'always' : 'never'}
        selectedValuesDescriptionId={selectedValuesDescriptionId}
        textAlign={textAlign}
        textTagListData={textTags}
        userInput={userInput}
        // In tablet, we would want to show the virtual keyboard
        virtualKeyboard={isTablet() ? virtualKeyboard : undefined}
        {...inputEventHandlers}
        {...inputProps}
        {...textTagListProps}
      />
    ) : (
      <SelectMobileFieldInput
        aria-controls={dropdownId}
        aria-describedby={inputProps['aria-describedby']}
        aria-expanded={isDropdownOpen}
        aria-invalid={inputProps['aria-invalid']}
        aria-label={ariaLabel}
        aria-labelledby={labelProps.id}
        displayValue={displayValue}
        hasEmptyLabel={hasEmptyLabel}
        hasInsideLabel={hasInsideLabel}
        isRequired={isRequired}
        onBlur={inputProps.onBlur}
        onFocus={inputProps.onFocus}
        placeholder={placeholder}
        ref={enabledElementRef as Ref<HTMLDivElement>}
        textAlign={textAlign}
        variant={inputProps.variant}
      />
    );

    const dropdown = !shouldShowDropdown ? null : isPhone() ? (
      <SelectMultipleMobileDropdown
        aria-describedby={ariaProps.ariaDescribedBy}
        aria-label={ariaLabel}
        assistiveText={assistiveText}
        data={data}
        dropdownId={dropdownId}
        enabledElementRef={enabledElementRef}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        isAddToListShown={isAddToListShown}
        isDisabled={isDisabled}
        isEmptyResults={isEmptyResults}
        isFocused={isFocused}
        isLoading={isLoading}
        isOpen={shouldShowDropdown}
        isReadonly={isReadonly}
        isRequired={isRequired}
        isSelectedOnlyView={isDropdownSelectedOnlyView}
        isUserFiltering={isUserFiltering}
        itemRenderer={itemRenderer}
        itemText={itemText}
        label={label}
        liveRegionText={liveRegionText}
        placeholder={placeholder}
        propIsLoading={propIsLoading}
        searchText={searchText}
        selectedValuesDescriptionId={selectedValuesDescriptionId}
        setDropdownOpen={setDropdownOpen}
        setUserInput={setUserInput}
        stopFiltering={stopFiltering}
        textAlign={textAlign}
        textTagListRef={textTagListProps.textTagListRef}
        userAssistanceDensity={userAssistanceDensity}
        userInput={userInput}
        valueItems={valueItems}
        virtualKeyboard={virtualKeyboard}
        onAddToListAction={addToListEventHandlers.onAction}
        onCommit={onCommit}
        onFieldBlur={focusProps.onfocusout}
        onFieldFocus={focusProps.onfocusin}
        onFieldInput={inputEventHandlers.onInput}
        onFieldKeyDown={inputEventHandlers.onKeyDown}
        onFieldKeyUp={inputEventHandlers.onKeyUp}
        onLoadRange={propOnLoadRange}
        onSelectedValuesCountKeyDown={selectedValuesCountProps.onKeyDown}
        onSelectedValuesCountKeyUp={selectedValuesCountProps.onKeyUp}
        onSelectedValuesCountMouseDown={selectedValuesCountProps.onMouseDown}
        onSelectedValuesCountToggle={selectedValuesCountProps.onToggle}
      />
    ) : (
      <SelectMultipleDesktopDropdown
        {...collectionProps}
        {...dropdownEventHandlers}
        assistiveText={assistiveText}
        dropdownId={dropdownId}
        dropdownRef={dropdownRef}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        isAddToListShown={isAddToListShown}
        isDisabled={isDisabled}
        isDropdownAbove={isDropdownAbove}
        isEmptyResults={isEmptyResults}
        isOpen={shouldShowDropdown}
        isReadonly={isReadonly}
        isSelectedOnlyView={isDropdownSelectedOnlyView}
        isUserFiltering={isUserFiltering}
        itemRenderer={itemRenderer}
        itemText={itemText}
        label={label}
        mainFieldRef={mainFieldRef}
        onAddToListAction={addToListEventHandlers.onAction}
        searchText={searchText}
        userAssistanceDensity={userAssistanceDensity}
      />
    );

    return (
      <Fragment>
        <FormFieldContext.Provider value={formFieldContext}>
          <SelectMultipleField
            columnSpan={columnSpan}
            compactUserAssistance={compactUserAssistance}
            fieldLabel={label}
            hasInsideLabel={hasInsideLabel}
            inlineUserAssistance={inlineUserAssistance}
            isDropdownArrowShown={true}
            isDropdownSelectedOnlyView={isDropdownSelectedOnlyView}
            isSelectedValuesCountShown={hasSelectedValuesCount}
            mainFieldRef={mainFieldRef}
            onBlur={focusProps.onfocusout}
            onFocus={focusProps.onfocusin}
            onDropdownArrowClick={dropdownArrowEventHandlers.onClick}
            onSelectedValuesCountKeyDown={selectedValuesCountProps.onKeyDown}
            onSelectedValuesCountKeyUp={selectedValuesCountProps.onKeyUp}
            onSelectedValuesCountMouseDown={selectedValuesCountProps.onMouseDown}
            onSelectedValuesCountToggle={selectedValuesCountProps.onToggle}
            selectedValuesCount={valueItems?.length}
            testId={testId}
            {...fieldLabelProps}
            {...mouseProps}
            {...textFieldProps}>
            {mainFieldInput}
          </SelectMultipleField>
        </FormFieldContext.Provider>
        {dropdown}
      </Fragment>
    );
  }
);
